import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry } from 'ag-grid-community'
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise'
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise'
import { Button, Modal, message } from 'antd'

import Spinner from 'components/antD/spin'
import { getProgramList, deleteProgram } from 'api/program.api'
import { getTutorList, deleteTutor } from 'api/tutor.api'
import { getToolsInventoryList } from 'api/inventory.api'
import { getToolsRentalGrid } from 'api/rental.api'
import { getGridEditConfig } from 'pages/dataTable/gridEditConfig'
import { useAuthStore } from 'store/useAuthStore'

import styles from './agGrid.module.scss'

ModuleRegistry.registerModules([AllEnterpriseModule.with(AgChartsEnterpriseModule)])
LicenseManager.setLicenseKey(
    'Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-090332}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Domfam_Co.,_Ltd.}_is_granted_a_{Multiple_Applications}_Developer_License_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Charts_and_AG_Grid}_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{20_July_2026}____[v3]_[0102]_MTc4NDUwMjAwMDAwMA==f9eb0f450dab63a75694821bc1436af6',
)

// 인라인 편집: 변경된 행에만 저장/취소 노출 (행 식별자는 context.idField 로 주입)
const EditActionsCell = ({ data, context, node }) => {
    const id = data?.[context?.idField]
    if (!context?.editMode || !context?.dirtyIds?.has(id)) return null
    return (
        <span className={styles.rowActions}>
            <button type="button" className={styles.saveBtn} onClick={() => context.onSaveRow(node)}>
                저장
            </button>
            <button type="button" className={styles.cancelBtn} onClick={() => context.onCancelRow(node)}>
                취소
            </button>
        </span>
    )
}

/**
 * 편집 모드에서 수정 가능/불가 셀을 시각적으로 구분하고, 안내가 있는 헤더에 표시를 붙인다.
 * applyEditing 이 editable 을 넣은 컬럼 = 수정 가능, 그 외 = 잠김.
 * 페이지별 설정과 무관하게 여기서 일괄 처리하므로 새 페이지를 추가해도 자동 적용된다.
 * 컬럼이 이미 cellClass / headerClass 를 가지고 있으면 덮어쓰지 않고 함께 적용한다.
 */
const markEditableCells = (cols, editMode) =>
    cols.map(col => {
        if (col.children) return { ...col, children: markEditableCells(col.children, editMode) }
        if (!col.field) return col

        const own = col.cellClass
        const { editable } = col
        // 편집 규칙 안내이므로 조회 중에는 툴팁과 ⓘ 표시를 모두 감춘다.
        // 문장이 여러 개면 마침표 뒤에서 줄을 바꿔 한 줄로 길게 늘어지지 않게 한다.
        const headerTooltip = editMode && col.headerTooltip ? col.headerTooltip.replace(/\.\s+/g, '.\n') : undefined
        const headerClass = headerTooltip ? [].concat(col.headerClass || [], styles.hasNote) : col.headerClass
        return {
            ...col,
            headerTooltip,
            headerClass,
            cellClass: params => {
                const inherited = typeof own === 'function' ? own(params) : own
                if (!params.context?.editMode) return inherited
                // editable 이 함수면 행 값에 따라 달라지므로(예: 도서벽지 행의 기관명·주소) 실제로 호출해 판정한다
                const canEdit = typeof editable === 'function' ? !!editable(params) : !!editable
                const mark = canEdit ? styles.editableCell : styles.readonlyCell
                return inherited ? [].concat(inherited, mark) : mark
            },
        }
    })

const AgGrid = ({ pageType, gridType, colDefs, defaultColDef, region }) => {
    //  state
    const [agGridListData, setAgGridListData] = useState([])
    const [isLoading, setLoading] = useState(true)
    const [editMode, setEditMode] = useState(false)
    const [isEnteringEdit, setEnteringEdit] = useState(false) // 편집 모드 진입 중(최신 데이터 재조회)
    const [dirtyCount, setDirtyCount] = useState(0) // 미저장 변경 행 수 (전체 저장 버튼 라벨용)
    const [selectedIds, setSelectedIds] = useState(() => new Set()) // 삭제용 체크박스 선택 행 id
    const [isDeleting, setDeleting] = useState(false)
    const gridRef = useRef(null)
    const editModeRef = useRef(false)
    const dirtyRef = useRef(new Set()) // 변경된 행 식별자 집합
    const originalRef = useRef({}) // 편집 전 원본 스냅샷 (취소용)
    const changedFieldsRef = useRef({}) // 행 id → 실제 변경된 필드 Set (변경분만 검증)
    const failedIdsRef = useRef(new Set()) // 저장 실패한 행 id (행 강조용)
    const newRowIdsRef = useRef(new Set()) // 그리드에 추가했지만 아직 서버에 저장 안 된 행의 임시 id
    const newRowSeqRef = useRef(0) // 임시 id 발급용 카운터
    const { userInfo } = useAuthStore()

    /*
     * 가로 스크롤바를 그리드 위에도 하나 더 둔다.
     * ag-grid 는 기본적으로 가로 스크롤바가 그리드 맨 아래(전체 행 아래)에만 있어서,
     * domLayout="autoHeight" 처럼 행이 많아 그리드 자체가 길어지면 스크롤바를 쓰려고
     * 페이지 맨 아래까지 내려야 한다. 위쪽에 똑같이 움직이는 가짜 스크롤바를 두고
     * 실제 그리드의 가로 스크롤 위치와 서로 동기화한다.
     */
    const agGridWrapperRef = useRef(null)
    const topScrollRef = useRef(null)
    const topScrollInnerRef = useRef(null)
    const isSyncingScrollRef = useRef(false)

    const syncTopScrollWidth = useCallback(() => {
        const api = gridRef.current?.api
        if (!api || !topScrollInnerRef.current) return
        const totalWidth = api.getAllDisplayedColumns().reduce((sum, col) => sum + col.getActualWidth(), 0)
        topScrollInnerRef.current.style.width = `${totalWidth}px`
    }, [])

    const onBodyScroll = useCallback(params => {
        if (params.direction !== 'horizontal' || isSyncingScrollRef.current) return
        if (!topScrollRef.current) return
        isSyncingScrollRef.current = true
        topScrollRef.current.scrollLeft = params.left
        isSyncingScrollRef.current = false
    }, [])

    const handleTopScroll = useCallback(() => {
        if (isSyncingScrollRef.current) return
        const viewport = agGridWrapperRef.current?.querySelector('.ag-center-cols-viewport')
        if (!viewport || !topScrollRef.current) return
        isSyncingScrollRef.current = true
        viewport.scrollLeft = topScrollRef.current.scrollLeft
        isSyncingScrollRef.current = false
    }, [])

    // 페이지별 편집 설정 (없으면 읽기 전용 그리드)
    const editCfg = useMemo(() => getGridEditConfig(pageType, gridType), [pageType, gridType])
    const isEditable = !!editCfg
    const rowIdOf = useCallback(data => data?.[editCfg?.idField], [editCfg])

    // 등록·삭제(SWLINK26-46)는 강사/교육 그리드에서만 지원 — 백엔드에 단건 생성·삭제 API가 있는 대상만
    const canRegisterDelete = gridType === 'normal' && (pageType === 'tutor' || pageType === 'program')
    const deleteFn = pageType === 'tutor' ? deleteTutor : deleteProgram

    const syncDirtyCount = useCallback(() => setDirtyCount(dirtyRef.current.size), [])
    const getRowId = useCallback(params => String(rowIdOf(params.data)), [rowIdOf])
    // 저장 실패 행 강조 (빨간 배경)
    const getRowStyle = useCallback(
        params => (failedIdsRef.current.has(rowIdOf(params.data)) ? { background: '#fff1f0' } : undefined),
        [rowIdOf],
    )

    const refreshActionsCell = useCallback(
        node => {
            gridRef.current?.api?.refreshCells({
                rowNodes: [node],
                columns: [editCfg?.actionsColId],
                force: true,
            })
        },
        [editCfg],
    )

    const handleRowClick = event => {
        if (editModeRef.current) return // 편집 모드에서는 상세 이동 비활성
        if (event.column?.getColId() === 'ag-Grid-SelectionColumn') return // 삭제용 체크박스 클릭 시 상세 이동 비활성
        const { tutorId, programId, rentalId } = event.data
        const inventoryId = event.data.toolsId
        if (rentalId) {
            window.open(`/tools/rental/${rentalId}`, '_blank', 'fullscreen=yes')
            return
        }
        if (tutorId) {
            window.open(`/tutors/${tutorId}`, '_blank', 'fullscreen=yes')
        }
        if (programId) {
            window.open(`/program/${programId}`, '_blank', 'fullscreen=yes')
        }
        if (inventoryId) {
            window.open(`/tools/inventory/${inventoryId}`, '_blank', 'fullscreen=yes')
        }
    }

    // api
    // silent=true 면 스피너를 띄우지 않는다(편집 모드 진입 시 그리드를 언마운트하지 않기 위함)
    const handleFetchData = useCallback(async ({ silent = false } = {}) => {
        try {
            if (!silent) setLoading(true)
            let allData

            switch (pageType) {
                case 'program': {
                    const { data } = await getProgramList({ pageNo: 0, pageSize: 0, regionId: region })
                    const programs = data?.data?.programs
                    if (programs) {
                        allData = programs
                    }
                    break
                }
                case 'tutor': {
                    const { data } = await getTutorList({ pageNo: 0, pageSize: 0, regionId: region })
                    const tutors = data?.data?.tutors
                    if (tutors) {
                        allData = tutors
                    }
                    break
                }
                case 'inventory': {
                    const { data } = await getToolsInventoryList({ pageNo: 0, pageSize: 0 })
                    const tools = data?.data?.tools
                    if (tools) {
                        allData = tools
                    }
                    break
                }
                case 'rental': {
                    const { data } = await getToolsRentalGrid()
                    const rentals = data?.data
                    if (rentals) {
                        allData = rentals
                    }
                    break
                }
                case 'statistics_program':
                case 'statistics_tutor':
                    break
                default: {
                    break
                }
            }
            if (allData) {
                setAgGridListData(allData)
            }
        } catch (e) {
            console.error('Unexpected error:', e)
        } finally {
            if (!silent) setLoading(false)
        }
    }, [pageType, region])

    useEffect(() => {
        handleFetchData()
    }, [handleFetchData])

    /* ----------------------------- 인라인 편집 ----------------------------- */
    const onCellEditingStarted = useCallback(
        params => {
            const id = rowIdOf(params.data)
            if (id != null && !(id in originalRef.current)) {
                originalRef.current[id] = { ...params.data } // 첫 편집 시 원본 스냅샷
            }
        },
        [rowIdOf],
    )

    const onCellValueChanged = useCallback(
        params => {
            const id = rowIdOf(params.data)
            if (id == null) return

            const field = params.colDef?.field
            if (!changedFieldsRef.current[id]) changedFieldsRef.current[id] = new Set()
            const changedFields = changedFieldsRef.current[id]

            /*
             * 취소용 원본 스냅샷은 여기서 확정한다.
             *
             * onCellEditingStarted 의 스냅샷만으로는 부족하다. 두 가지 구멍이 있다.
             *  1) 체크박스(boolean 셀)는 행 데이터를 먼저 바꾸고 나서 편집 시작 이벤트를 쏜다.
             *     그래서 그때 뜬 스냅샷에는 이미 새 값이 들어 있다.
             *  2) 채우기 핸들 드래그·붙여넣기·Delete 는 편집 시작 이벤트 자체가 없다.
             *
             * 대신 이 이벤트의 oldValue 는 항상 '바뀌기 직전 값'이므로, 셀이 처음 바뀔 때
             * 그 값을 스냅샷에 박아 두면 시점에 의존하지 않는다.
             * valueGetter 가 있는 컬럼은 oldValue 가 표시용 값이라(고용 여부의 '신규' 등)
             * 데이터에 되돌릴 수 없어 건너뛴다 — 이쪽은 편집 시작 스냅샷이 정확하다.
             */
            if (!(id in originalRef.current)) originalRef.current[id] = { ...params.data }
            if (field && !changedFields.has(field) && !params.colDef.valueGetter) {
                originalRef.current[id][field] = params.oldValue
            }

            if (field) changedFields.add(field)
            dirtyRef.current.add(id)
            // 재편집 시 실패 강조 해제
            if (failedIdsRef.current.delete(id)) params.api.redrawRows({ rowNodes: [params.node] })
            syncDirtyCount()
            refreshActionsCell(params.node)
        },
        [refreshActionsCell, syncDirtyCount, rowIdOf],
    )

    const clearRowDirty = useCallback(
        (node, id) => {
            dirtyRef.current.delete(id)
            delete originalRef.current[id]
            delete changedFieldsRef.current[id]
            syncDirtyCount()
            gridRef.current?.api?.refreshCells({ rowNodes: [node], force: true })
        },
        [syncDirtyCount],
    )

    // 저장 실패 행 강조 on/off
    const markFailed = useCallback((node, id) => {
        failedIdsRef.current.add(id)
        gridRef.current?.api?.redrawRows({ rowNodes: [node] })
    }, [])
    const clearFailed = useCallback((node, id) => {
        if (failedIdsRef.current.delete(id)) gridRef.current?.api?.redrawRows({ rowNodes: [node] })
    }, [])

    // 백엔드 실패 사유를 그대로 노출 (필수값 누락 등). userMessage 는 저장 전 단계에서 만든 안내문.
    const getSaveErrMsg = e =>
        e?.userMessage ||
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        '저장에 실패했습니다. 잠시 후 다시 시도해주세요.'

    /**
     * 저장 바디 생성.
     * 수정 API 가 전체 갱신이라, 목록 응답에 없는 필드(교육실적의 강사 매칭 등)는
     * 저장 직전에 원본을 다시 받아 바탕으로 깔아야 서버에서 지워지지 않는다.
     * 원본 조회에 실패하면 저장하지 않는다 — 그대로 보내면 그 필드들이 날아간다.
     */
    const buildDTO = useCallback(
        async (row, id) => {
            if (!editCfg.loadFull) return editCfg.toDTO(row)
            const full = await editCfg.loadFull(id).catch(() => null)
            if (!full) {
                throw Object.assign(new Error('load-full-failed'), {
                    userMessage: '저장 전 원본을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.',
                })
            }
            return editCfg.toDTO(row, full)
        },
        [editCfg],
    )

    // 변경분만 검증 → 첫 에러 메시지('' = 통과)
    const validateNode = useCallback(
        node => {
            const row = node?.data
            const changed = changedFieldsRef.current[rowIdOf(row)]
            if (!changed || !editCfg) return ''
            return (
                Array.from(changed)
                    .map(key => editCfg.validateField(key, row[key], row))
                    .find(Boolean) || ''
            )
        },
        [editCfg, rowIdOf],
    )

    // 페이지별 추가 검증(중복 등). 설정의 rowChecks 를 순서대로 돌려 첫 에러 반환.
    const runRowChecks = useCallback(
        node => {
            const row = node?.data
            const changed = changedFieldsRef.current[rowIdOf(row)]
            const checks = editCfg?.rowChecks
            if (!checks?.length) return ''
            return checks.map(fn => fn(row, gridRef.current?.api, changed)).find(Boolean) || ''
        },
        [editCfg, rowIdOf],
    )

    /*
     * 행 하나의 저장 전 에러 메시지.
     * 기존 행은 실제로 건드린 필드만(changedFields) 검증하지만, 아직 저장 안 된 새 행은
     * 사용자가 손대지 않은 필드도 기본값이 아닌 값으로 채웠는지 전부 확인해야 한다(validateRow).
     */
    const getRowError = useCallback(
        node => {
            const id = rowIdOf(node?.data)
            if (newRowIdsRef.current.has(id)) {
                const { errors } = editCfg.validateRow(node.data)
                return Object.values(errors)[0] || ''
            }
            return validateNode(node) || runRowChecks(node)
        },
        [editCfg, rowIdOf, validateNode, runRowChecks],
    )

    const onSaveRow = useCallback(
        async node => {
            const id = rowIdOf(node.data)
            const isNew = newRowIdsRef.current.has(id)
            const err = getRowError(node)
            if (err) {
                message.error(err)
                return
            }
            try {
                if (isNew) {
                    const newId = await editCfg.create(editCfg.toDTO(node.data))
                    message.success('등록되었습니다.')
                    newRowIdsRef.current.delete(id)
                    clearFailed(node, id)
                    clearRowDirty(node, id)
                    // 새로 만든 임시 행은 실제 id 를 모르니 그리드에서 걷어내고,
                    // 첨부파일·강사매칭 등 그리드로 못 채우는 부분은 상세 화면에서 이어 입력한다.
                    gridRef.current?.api?.applyTransaction({ remove: [node.data] })
                    if (newId != null) {
                        window.open(
                            pageType === 'tutor' ? `/tutors/${newId}` : `/program/${newId}`,
                            '_blank',
                            'fullscreen=yes',
                        )
                    }
                    // 다른 미저장 변경이 없을 때만 최신 목록으로 갱신(있으면 편집 중인 값이 날아간다)
                    if (dirtyRef.current.size === 0) await handleFetchData({ silent: true })
                } else {
                    await editCfg.save(id, await buildDTO(node.data, id))
                    message.success('저장되었습니다.')
                    clearFailed(node, id)
                    clearRowDirty(node, id)
                    if (dirtyRef.current.size === 0) await handleFetchData({ silent: true })
                }
            } catch (e) {
                console.error(`${editCfg.label} ${isNew ? '등록' : '저장'} 실패:`, e)
                message.error(getSaveErrMsg(e))
                markFailed(node, id)
            }
        },
        [editCfg, rowIdOf, getRowError, buildDTO, clearRowDirty, clearFailed, markFailed, handleFetchData, pageType],
    )

    // 전체 저장: 확인 → 전체 검증(형식+동명이인, 새 행은 전체 필수값) → 순차 PATCH/POST. 성공 행은 해제, 실패 행은 강조 유지.
    const runSaveAll = useCallback(
        async nodes => {
            // 1) 검증
            const invalid = nodes.map(n => ({ n, err: getRowError(n) })).filter(x => x.err)
            if (invalid.length) {
                message.error(`검증 실패 ${invalid.length}건 — 예: ${invalid[0].err}`)
                return
            }
            // 2) 순차 PATCH(기존 행)/POST(새 행)
            let ok = 0
            const failed = []
            const newlyCreated = []
            await nodes.reduce(
                (chain, node) =>
                    chain.then(async () => {
                        const id = rowIdOf(node.data)
                        const isNew = newRowIdsRef.current.has(id)
                        try {
                            if (isNew) {
                                const newId = await editCfg.create(editCfg.toDTO(node.data))
                                newRowIdsRef.current.delete(id)
                                gridRef.current?.api?.applyTransaction({ remove: [node.data] })
                                if (newId != null) newlyCreated.push(newId)
                            } else {
                                await editCfg.save(id, await buildDTO(node.data, id))
                            }
                            clearFailed(node, id)
                            clearRowDirty(node, id)
                            ok += 1
                        } catch (e) {
                            console.error(`${editCfg.label} ${isNew ? '등록' : '저장'} 실패:`, id, e)
                            markFailed(node, id)
                            failed.push(getSaveErrMsg(e))
                        }
                    }),
                Promise.resolve(),
            )
            // 건별로 저장하므로 일부만 성공할 수 있습니다. 되돌리기가 없으니 남은 작업을 분명히 알립니다.
            if (failed.length === 0) {
                message.success(`전체 저장 완료 (${ok}건)`)
            } else {
                message.warning({
                    content: `${ok}건 저장, ${failed.length}건 실패입니다. 성공한 건은 이미 반영되었습니다. 실패한 행(빨간색)만 수정 후 다시 저장해주세요. — ${failed[0]}`,
                    duration: 8,
                })
            }
            // 새로 등록한 행이 있고 다른 미저장 변경이 안 남았으면 최신 목록으로 갱신
            if (newlyCreated.length && dirtyRef.current.size === 0) await handleFetchData({ silent: true })
            // 첨부파일 등 그리드로 못 채우는 부분은 상세 화면에서 이어 입력한다.
            newlyCreated.forEach(newId =>
                window.open(pageType === 'tutor' ? `/tutors/${newId}` : `/program/${newId}`, '_blank', 'fullscreen=yes'),
            )
        },
        [editCfg, rowIdOf, buildDTO, getRowError, clearRowDirty, clearFailed, markFailed, handleFetchData, pageType],
    )

    const onSaveAll = useCallback(() => {
        const api = gridRef.current?.api
        if (!api) return
        const nodes = Array.from(dirtyRef.current)
            .map(id => api.getRowNode(String(id)))
            .filter(Boolean)
        if (!nodes.length) return
        Modal.confirm({
            title: '변경사항 전체 저장',
            content: `${nodes.length}건의 변경사항을 저장하시겠습니까?`,
            okText: '저장',
            cancelText: '취소',
            onOk: () => runSaveAll(nodes),
        })
    }, [runSaveAll])

    /* ----------------------------- 선택 삭제 (SWLINK26-46) ----------------------------- */
    // 백엔드가 단건 삭제만 지원해서 전체 저장과 같은 순차 처리 패턴을 그대로 따른다.
    // 아직 저장 안 된 새 행이 선택되어 있으면 API 호출 없이 그리드에서만 제거한다.
    const runDeleteSelected = useCallback(
        async ids => {
            setDeleting(true)
            const newIds = ids.filter(id => newRowIdsRef.current.has(id))
            const persistedIds = ids.filter(id => !newRowIdsRef.current.has(id))

            if (newIds.length) {
                const rowsToRemove = newIds
                    .map(id => gridRef.current?.api?.getRowNode(String(id))?.data)
                    .filter(Boolean)
                gridRef.current?.api?.applyTransaction({ remove: rowsToRemove })
                newIds.forEach(id => {
                    newRowIdsRef.current.delete(id)
                    dirtyRef.current.delete(id)
                    delete changedFieldsRef.current[id]
                })
                syncDirtyCount()
            }

            let ok = newIds.length
            const failed = []
            await persistedIds.reduce(
                (chain, id) =>
                    chain.then(async () => {
                        try {
                            await deleteFn(id)
                            ok += 1
                        } catch (e) {
                            console.error(`${pageType} 삭제 실패:`, id, e)
                            failed.push(getSaveErrMsg(e))
                        }
                    }),
                Promise.resolve(),
            )
            setDeleting(false)
            setSelectedIds(new Set())
            gridRef.current?.api?.deselectAll()
            if (failed.length === 0) {
                message.success(`삭제 완료 (${ok}건)`)
            } else {
                message.warning({
                    content: `${ok}건 삭제, ${failed.length}건 실패입니다. — ${failed[0]}`,
                    duration: 8,
                })
            }
            if (persistedIds.length) await handleFetchData({ silent: true })
        },
        [deleteFn, pageType, handleFetchData, syncDirtyCount],
    )

    const onDeleteSelected = useCallback(() => {
        const ids = Array.from(selectedIds)
        if (!ids.length) return
        Modal.confirm({
            title: '선택 항목 삭제',
            content: `선택한 ${ids.length}건을 삭제하시겠습니까? 삭제된 데이터는 복구할 수 없습니다.`,
            okText: '삭제',
            okButtonProps: { danger: true },
            cancelText: '취소',
            onOk: () => runDeleteSelected(ids),
        })
    }, [selectedIds, runDeleteSelected])

    const onSelectionChanged = useCallback(() => {
        const api = gridRef.current?.api
        if (!api) return
        setSelectedIds(new Set(api.getSelectedRows().map(rowIdOf)))
    }, [rowIdOf])

    const onCancelRow = useCallback(
        node => {
            const id = rowIdOf(node.data)
            failedIdsRef.current.delete(id)
            // 아직 저장 안 된 새 행은 되돌릴 원본이 없다 — 행 자체를 걷어낸다.
            if (newRowIdsRef.current.has(id)) {
                newRowIdsRef.current.delete(id)
                dirtyRef.current.delete(id)
                delete changedFieldsRef.current[id]
                syncDirtyCount()
                gridRef.current?.api?.applyTransaction({ remove: [node.data] })
                return
            }
            if (id != null && originalRef.current[id]) {
                node.setData({ ...originalRef.current[id] }) // setData가 getRowStyle 재평가 → 강조 해제
            }
            dirtyRef.current.delete(id)
            delete originalRef.current[id]
            delete changedFieldsRef.current[id]
            syncDirtyCount()
            gridRef.current?.api?.refreshCells({ rowNodes: [node], force: true })
        },
        [syncDirtyCount, rowIdOf],
    )

    /* ----------------------------- 새 행 등록 (SWLINK26-46 개선) ----------------------------- */
    // 편집모드 columnDefs(저장/취소 액션 컬럼)가 반영된 뒤에 행을 넣어야 해서 toggleEditMode 와 분리한다.
    const insertNewRow = useCallback(() => {
        if (!editCfg?.newRowDefaults) return
        newRowSeqRef.current -= 1
        // 식별자 컬럼(programId/tutorId)이 숫자 컬럼으로 추론돼 있어, 문자열 임시 id는 "Invalid Number"로
        // 보인다. 실제 서버 id(양수)와 절대 겹치지 않는 음수를 임시 id로 쓴다.
        const tempId = newRowSeqRef.current
        const row = { ...editCfg.newRowDefaults(userInfo?.regionId), [editCfg.idField]: tempId }
        newRowIdsRef.current.add(tempId)
        dirtyRef.current.add(tempId)
        syncDirtyCount()
        gridRef.current?.api?.applyTransaction({ add: [row], addIndex: 0 })
        gridRef.current?.api?.ensureIndexVisible(0)
    }, [editCfg, syncDirtyCount, userInfo])

    const onAddRow = useCallback(async () => {
        if (!editModeRef.current) {
            setEnteringEdit(true)
            try {
                await handleFetchData({ silent: true })
            } finally {
                setEnteringEdit(false)
            }
            setEditMode(true)
            editModeRef.current = true
            // 편집모드 전환으로 컬럼(저장/취소 액션 컬럼)이 다시 그려진 다음 틱에 삽입한다.
            setTimeout(insertNewRow, 0)
        } else {
            insertNewRow()
        }
    }, [handleFetchData, insertNewRow])

    const toggleEditMode = useCallback(async () => {
        if (editMode && dirtyRef.current.size > 0) {
            message.warning('저장 또는 취소되지 않은 변경사항이 있습니다. 먼저 처리해주세요.')
            return
        }
        const next = !editMode

        // 편집 시작 전 최신 데이터로 갱신.
        // 수정 API 가 전체 갱신(부분 수정 아님)이라, 오래 열어둔 화면의 낡은 값으로 저장하면
        // 그 사이 다른 사람이 바꾼 필드까지 조용히 덮어쓴다. 편집은 항상 최신 기준에서 시작한다.
        //
        // 그리드를 그대로 두려고 스피너 없이(silent) 받으므로, 대신 버튼에 진행 표시를 띄운다.
        // 데이터가 많으면 몇 초씩 걸려 반응이 없는 것처럼 보인다.
        if (next) {
            setEnteringEdit(true)
            try {
                await handleFetchData({ silent: true })
            } finally {
                setEnteringEdit(false)
            }
        }

        setEditMode(next)
        editModeRef.current = next
        setTimeout(() => gridRef.current?.api?.refreshCells({ force: true }), 0)
    }, [editMode, handleFetchData])

    /*
     * 미저장 변경이 있는 상태에서 새로고침·탭 닫기로 값이 날아가지 않도록 브라우저 경고를 띄운다.
     * '편집 종료' 버튼은 위에서 막지만 브라우저 동작은 막을 수 없어서 따로 둔다.
     * 앱 안의 메뉴 이동은 여기서 못 막는다(BrowserRouter 라 useBlocker 를 쓸 수 없음).
     */
    useEffect(() => {
        if (!editMode) return undefined
        const handleBeforeUnload = event => {
            if (dirtyRef.current.size === 0) return
            event.preventDefault()
            event.returnValue = '' // 크롬은 returnValue 가 있어야 확인창을 띄운다
        }
        window.addEventListener('beforeunload', handleBeforeUnload)
        return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    }, [editMode])

    // 편집용 컬럼 + 액션 컬럼
    const gridColDefs = useMemo(() => {
        if (!editCfg) return colDefs
        const base = markEditableCells(editCfg.applyEditing(colDefs), editMode)
        if (!editMode) return base
        return [
            ...base,
            {
                headerName: '',
                colId: editCfg.actionsColId,
                pinned: 'right',
                width: 130,
                editable: false,
                sortable: false,
                filter: false,
                resizable: false,
                suppressHeaderMenuButton: true,
                cellRenderer: EditActionsCell,
            },
        ]
    }, [colDefs, editCfg, editMode])

    const gridContext = useMemo(
        () => ({ editMode, dirtyIds: dirtyRef.current, onSaveRow, onCancelRow, idField: editCfg?.idField }),
        [editMode, onSaveRow, onCancelRow, editCfg],
    )

    return (
        <div className={styles.container} style={{ height: gridType === 'pivot' ? '1000px' : 'auto' }}>
            {(isEditable || canRegisterDelete) && !isLoading && (
                <div className={styles.editBar}>
                    <div className={styles.editToolbar}>
                        {canRegisterDelete && (
                            <Button loading={isEnteringEdit} onClick={onAddRow}>
                                등록
                            </Button>
                        )}
                        {canRegisterDelete && (
                            <Button
                                danger
                                disabled={editMode || selectedIds.size === 0}
                                loading={isDeleting}
                                onClick={onDeleteSelected}
                            >
                                선택 삭제{selectedIds.size > 0 ? ` (${selectedIds.size}건)` : ''}
                            </Button>
                        )}
                        {/* loading 중에는 antd 가 클릭을 막아 중복 진입도 함께 방지된다 */}
                        {isEditable && (
                            <Button
                                type={editMode ? 'primary' : 'default'}
                                loading={isEnteringEdit}
                                onClick={toggleEditMode}
                            >
                                {isEnteringEdit ? '편집 모드 준비 중' : editMode ? '편집 종료' : '편집 모드'}
                            </Button>
                        )}
                        {isEditable && editMode && (
                            <Button type="primary" ghost disabled={dirtyCount === 0} onClick={onSaveAll}>
                                변경사항 전체 저장{dirtyCount > 0 ? ` (${dirtyCount}건)` : ''}
                            </Button>
                        )}
                    </div>
                    {/* 편집 규칙 범례 — 셀 색을 견본으로 보여줘 문장을 읽지 않아도 알 수 있게 한다 */}
                    {editMode && (
                        <div className={styles.editLegend}>
                            <span className={styles.legendItem}>
                                <i className={styles.swatchEditable} />
                                <span>
                                    <b>수정 가능</b> — 셀을 클릭하면 항목에 맞는 입력 방식(입력창·드롭다운·
                                    체크박스)이 나타납니다.
                                </span>
                            </span>
                            <span className={styles.legendItem}>
                                <i className={styles.swatchReadonly} />
                                <span>
                                    <b>수정 불가</b> — 자동으로 계산되거나 상세 화면에서만 변경할 수 있는
                                    항목입니다.
                                </span>
                            </span>
                            <span className={styles.legendItem}>
                                <i className={styles.legendNote}>ⓘ</i>
                                <span>
                                    <b>입력 규칙 안내</b> — 열 제목의 ⓘ에 마우스를 올리면 항목별 규칙을 확인할 수
                                    있습니다.
                                </span>
                            </span>
                        </div>
                    )}
                </div>
            )}
            {isLoading ? (
                <div className={styles.spin_wrap}>
                    <Spinner />
                </div>
            ) : (
                <>
                    {/* 그리드 자체 가로 스크롤바(맨 아래)와 같은 위치를 보여주는 가짜 스크롤바 — 위에서도 가로로 넘길 수 있게 */}
                    <div className={styles.topScroll} ref={topScrollRef} onScroll={handleTopScroll}>
                        <div ref={topScrollInnerRef} className={styles.topScrollInner} />
                    </div>
                    <div ref={agGridWrapperRef}>
                    {gridType === 'normal' && (
                        <AgGridReact
                            ref={gridRef}
                            rowData={agGridListData}
                            columnDefs={gridColDefs}
                            defaultColDef={defaultColDef}
                            context={gridContext}
                            getRowId={isEditable ? getRowId : undefined}
                            getRowStyle={isEditable ? getRowStyle : undefined}
                            domLayout="autoHeight"
                            pagination
                            // 기본 100건은 편집 시 스크롤이 너무 길어져 20건으로 줄입니다(선택지로 변경 가능)
                            paginationPageSize={20}
                            paginationPageSizeSelector={[20, 50, 100, 200]}
                            cellSelection
                            rowGroupPanelShow="always"
                            enableCharts
                            stopEditingWhenCellsLoseFocus
                            // 편집 모드에서만 한 번 클릭으로 편집 시작(평소에는 클릭이 행 선택/상세 이동이라 켜면 안 됨)
                            singleClickEdit={isEditable && editMode}
                            // 삭제용 체크박스 선택 — 등록·삭제 지원 페이지에서만(SWLINK26-46)
                            rowSelection={canRegisterDelete ? { mode: 'multiRow', headerCheckbox: true } : undefined}
                            onSelectionChanged={canRegisterDelete ? onSelectionChanged : undefined}
                            // 헤더 툴팁(편집 규칙 안내)이 바로 뜨도록. 기본값 2000ms 는 안 뜨는 것처럼 느껴집니다
                            tooltipShowDelay={300}
                            onRowClicked={handleRowClick}
                            onCellEditingStarted={isEditable ? onCellEditingStarted : undefined}
                            onCellValueChanged={isEditable ? onCellValueChanged : undefined}
                            onBodyScroll={onBodyScroll}
                            onColumnResized={syncTopScrollWidth}
                            onDisplayedColumnsChanged={syncTopScrollWidth}
                            onGridSizeChanged={syncTopScrollWidth}
                            onGridReady={params => {
                                // 기본 필터: 당해연도만 ('연도' 컬럼 = colId registrationYearFilter)
                                params.api.setFilterModel({
                                    registrationYearFilter: {
                                        filterType: 'set',
                                        values: [String(new Date().getFullYear())],
                                    },
                                })
                                setTimeout(syncTopScrollWidth, 0)
                            }}
                        />
                    )}
                    {gridType === 'pivot' && (
                        <AgGridReact
                            ref={gridRef}
                            rowData={agGridListData}
                            columnDefs={colDefs}
                            defaultColDef={defaultColDef}
                            sideBar={['columns', 'filters']}
                            pivotPanelShow="always"
                            rowGroupPanelShow="always"
                            cellSelection
                            enableCharts
                            onBodyScroll={onBodyScroll}
                            onColumnResized={syncTopScrollWidth}
                            onDisplayedColumnsChanged={syncTopScrollWidth}
                            onGridSizeChanged={syncTopScrollWidth}
                            onGridReady={() => setTimeout(syncTopScrollWidth, 0)}
                            statusBar={{
                                statusPanels: [
                                    { statusPanel: 'agTotalRowCountComponent', align: 'left' },
                                    { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
                                    { statusPanel: 'agFilteredRowCountComponent' },
                                    { statusPanel: 'agSelectedRowCountComponent' },
                                    { statusPanel: 'agAggregationComponent' },
                                ],
                            }}
                        />
                    )}
                    </div>
                </>
            )}
        </div>
    )
}
export default AgGrid
